import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage, pick } from "@/i18n";
import { api } from "@/api";
import { useSEO } from "@/hooks/useSEO";
import ExperienceCard from "@/components/ExperienceCard";
import SectionHeader from "@/components/SectionHeader";

const FILTERS = ["all", "culture", "sea", "canoe", "nature", "food", "wine", "night", "private"];

export default function Experiences() {
  const { lang, t } = useLanguage();
  useSEO(t("seo_exp_title"), t("seo_exp_desc"));
  const [searchParams, setSearchParams] = useSearchParams();
  const [experiences, setExperiences] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const category = searchParams.get("category") || "all";
  const destination = searchParams.get("destination") || "";

  useEffect(() => {
    api.destinations().then(setDestinations).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "all") params.category = category;
    if (destination) params.destination = destination;
    api
      .experiences(params)
      .then((data) => {
        const destMap = Object.fromEntries(destinations.map((d) => [d.slug, d.name]));
        setExperiences(data.map((e) => ({ ...e, destination_name: destMap[e.slug] ? undefined : destMap[e.destination] })));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, destination, destinations]);

  const setFilter = (key, value) => {
    const next = Object.fromEntries(searchParams);
    if (value && value !== "all") next[key] = value;
    else delete next[key];
    setSearchParams(next);
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? experiences.filter((e) =>
        [pick(e.title, "it"), pick(e.title, "en"), pick(e.short, "it"), pick(e.short, "en")]
          .join(" ")
          .toLowerCase()
          .includes(q)
      )
    : experiences;

  return (
    <div className="py-20 lg:py-28" data-testid="experiences-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeader overline="Flegrea Experience" title={t("exp_page_title")} sub={t("exp_page_sub")} />

        <div className="flex flex-wrap items-center gap-3 mt-14" data-testid="category-filters">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("search_placeholder")}
            data-testid="search-input"
            className="w-full sm:w-64 border border-stone2 bg-ivory px-5 py-2.5 text-sm text-ink focus:outline-none focus:border-medblue mb-2 sm:mb-0"
          />
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f}`}
              onClick={() => setFilter("category", f)}
              className={`text-[11px] font-medium uppercase tracking-[0.15em] px-5 py-2.5 border transition-colors duration-300 ${
                category === f
                  ? "bg-ink text-ivory border-ink"
                  : "border-stone2 text-ink/60 hover:border-ink hover:text-ink"
              }`}
            >
              {t(`filter_${f}`)}
            </button>
          ))}
          <select
            data-testid="destination-filter"
            value={destination}
            onChange={(e) => setFilter("destination", e.target.value)}
            className="ml-auto text-[11px] font-medium uppercase tracking-[0.15em] px-4 py-2.5 border border-stone2 bg-ivory text-ink/70 focus:outline-none focus:border-medblue"
          >
            <option value="">{t("all_destinations")}</option>
            {destinations.map((d) => (
              <option key={d.slug} value={d.slug}>
                {pick(d.name, lang)}
              </option>
            ))}
          </select>
        </div>

        <p className="mt-8 text-xs text-ink/40 uppercase tracking-[0.2em]" data-testid="results-count">
          {filtered.length} {t("results")}
        </p>

        {loading ? (
          <p className="mt-20 text-center text-ink/40" data-testid="loading-indicator">{t("loading")}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14 mt-8">
            {filtered.map((exp) => (
              <ExperienceCard key={exp.slug} exp={exp} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
